import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { CrmTab, PosEntry, PosStatus, FunnelData, ActionList } from './types';
import {
  useCrmPipeline,
  useCreateActionList,
  toCreateActionListBody
} from 'apiTyped/hooks/v2.crm.query';
import { useV2Filters } from '../../context/V2FiltersContext';
import {
  V2ContextBar,
  ContextToolbar,
  FiltersBtn,
  FiltersBadge,
  FilterIcon,
  ResetBtn
} from '../../components/V2ContextBar';
import { Badge } from '../../components/Badge';
import { Drawer } from '../../components/Drawer';

const statusToVariant = (s: PosStatus) =>
  s === 'prospect' ? ('default' as const) : s;
import { Button } from '../../components/Button';
import { TabBar } from '../../components/TabBar';
import { SearchInput } from '../../components/SearchInput';
import { PageHeader } from '../../components/PageHeader';
import { ActivityFeed } from '../../components/ActivityFeed';
import type { ActivityItem } from '../../components/ActivityFeed';
import { ActivityDetailDrawer } from '../../components/ActivityDetailDrawer';
import { PipelineChart } from '../../components/PipelineChart';
import { PosDrawer } from './PosDrawer';
import {
  NewPosModal,
  UploadListModal,
  WriteEmailModal,
  SaveActionListModal
} from './CrmModals';
import { PosFilterModal } from '../../components/PosFilterModal';
import {
  DEFAULT_FILTERS,
  PosFilterState,
  countActiveFilters
} from '../../components/posFilterTypes';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatListDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function listProgress(list: ActionList) {
  let towin = 0,
    active = 0,
    lost = 0,
    prospect = 0;
  list.pos.forEach((p) => {
    if (p.currentStatus === 'towin') towin++;
    else if (p.currentStatus === 'active') active++;
    else if (p.currentStatus === 'lost') lost++;
    else if (p.currentStatus === 'prospect') prospect++;
  });
  const toWinTotal = towin + prospect;
  let startNonActive = 0,
    moved = 0;
  list.pos.forEach((p) => {
    if (p.initialStatus !== 'active') {
      startNonActive++;
      if (p.currentStatus === 'active') moved++;
    }
  });
  const successRate =
    startNonActive === 0 ? null : Math.round((moved / startNonActive) * 100);
  return {
    total: list.pos.length,
    toWinTotal,
    active,
    lost,
    successRate,
    moved,
    startNonActive
  };
}

function classifyChange(
  initial: PosStatus,
  current: PosStatus
): { type: 'positive' | 'negative' | 'pending' | 'neutral'; label: string } {
  if (initial === current) return { type: 'neutral', label: 'No change' };
  if (initial === 'lost' && current === 'active')
    return { type: 'positive', label: 'Regained' };
  if ((initial === 'prospect' || initial === 'towin') && current === 'active')
    return { type: 'positive', label: 'Won' };
  if (initial === 'active' && current === 'lost')
    return { type: 'negative', label: 'Lost' };
  if (current === 'lost') return { type: 'negative', label: 'Declined' };
  if (initial === 'lost' && current === 'towin')
    return { type: 'pending', label: "Competitor ID'd" };
  if (initial === 'prospect' && current === 'towin')
    return { type: 'pending', label: 'Progressing' };
  return { type: 'pending', label: 'Status changed' };
}

// ─── Main Component ────────────────────────────────────────────────────────

export const CrmSalesTracking: React.FC = () => {
  const { filters } = useV2Filters();
  const { isLoading, pos, actionLists, activity, funnel, refetch } =
    useCrmPipeline({
      market: filters.market,
      channel: filters.channel,
      brand: filters.brand
    });
  const createActionList = useCreateActionList();
  const [activeTab, setActiveTab] = useState<CrmTab>('pos');
  const [selectedPos, setSelectedPos] = useState<PosEntry | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newPosOpen, setNewPosOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [actionListModalOpen, setActionListModalOpen] = useState(false);
  const [posFilters, setPosFilters] = useState<PosFilterState>(DEFAULT_FILTERS);
  const [searchQuery, setSearchQuery] = useState('');
  const [activityUserFilter, setActivityUserFilter] = useState('all');
  const [activityDetailItem, setActivityDetailItem] =
    useState<ActivityItem | null>(null);
  const [activityDetailOpen, setActivityDetailOpen] = useState(false);

  const handleFilterChange = useCallback(
    (updater: (prev: PosFilterState) => PosFilterState) =>
      setPosFilters(updater),
    []
  );
  const handleFilterReset = useCallback(
    () => setPosFilters(DEFAULT_FILTERS),
    []
  );

  const activeFilterCount = useMemo(
    () => countActiveFilters(posFilters, filters.channel === 'on_trade'),
    [posFilters, filters.channel]
  );

  const [selectedList, setSelectedList] = useState<ActionList | null>(null);
  const [listDetailOpen, setListDetailOpen] = useState(false);

  const handleListClick = (list: ActionList) => {
    setSelectedList(list);
    setListDetailOpen(true);
  };

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const toggleSelection = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pos.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pos.map((p) => p.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const total = pos.length;
  const funnelTotal =
    funnel.prospect + funnel.towin + funnel.active + funnel.lost;

  const activityUsers = useMemo(
    () => [...new Set(activity.map((a) => a.user))].sort(),
    [activity]
  );

  const handleActivityClick = useCallback(
    (item: ActivityItem) => {
      if (item.targetType === 'action_list' && item.targetId != null) {
        const list = actionLists.find((al) => al.id === String(item.targetId));
        if (list) {
          handleListClick(list);
          return;
        }
      }
      if (item.targetType === 'pos' && item.targetId != null) {
        const posEntry = pos.find((p) => p.id === item.targetId);
        if (posEntry) {
          handlePosClick(posEntry);
          return;
        }
      }
      setActivityDetailItem(item);
      setActivityDetailOpen(true);
    },
    [actionLists, pos]
  );

  const handlePosClick = (pos: PosEntry) => {
    setSelectedPos(pos);
    setDrawerOpen(true);
  };

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="CRM Sales Tracking"
        subtitle="Track where your wines are placed, monitor competitors, and prioritize commercial actions."
      >
        <Button icon={<ExportIcon />}>Export</Button>
        <Button onClick={() => setUploadOpen(true)} icon={<UploadIcon />}>
          Upload list
        </Button>
        <Button
          variant="primary"
          onClick={() => setNewPosOpen(true)}
          icon={<PlusIcon />}
        >
          New POS
        </Button>
      </PageHeader>

      {/* Context bar with filters */}
      <V2ContextBar>
        <ContextToolbar>
          <FiltersBtn type="button" onClick={() => setFilterModalOpen(true)}>
            <FilterIcon />
            Filters
            <FiltersBadge $zero={activeFilterCount === 0}>
              {activeFilterCount}
            </FiltersBadge>
          </FiltersBtn>
          {activeFilterCount > 0 && (
            <ResetBtn type="button" onClick={handleFilterReset}>
              Reset
            </ResetBtn>
          )}
        </ContextToolbar>
      </V2ContextBar>

      {/* Overview: KPIs + Pipeline in one card */}
      <OverviewCard>
        <OverviewKpis>
          <KpiCell>
            <KpiLabel>POS in scope</KpiLabel>
            <KpiValue>{total}</KpiValue>
            <KpiDelta>→ in current view</KpiDelta>
          </KpiCell>
          <KpiCell>
            <KpiLabel>My wines placed</KpiLabel>
            <KpiValue>{pos.reduce((s, p) => s + p.yourRef, 0)}</KpiValue>
            <KpiDelta>→ avg per POS</KpiDelta>
          </KpiCell>
          <KpiCell>
            <KpiLabel>POS at risk</KpiLabel>
            <KpiValue>{pos.filter((p) => p.status === 'lost').length}</KpiValue>
            <KpiDelta $down>↓ of current view</KpiDelta>
          </KpiCell>
          <KpiCell>
            <KpiLabel>Competitor footprint</KpiLabel>
            <KpiValue>{pos.reduce((s, p) => s + p.competitors, 0)}</KpiValue>
            <KpiDelta>→ competitor wines tracked</KpiDelta>
          </KpiCell>
        </OverviewKpis>
        <OverviewPipeline>
          <PipelineChart
            title="Pipeline"
            meta={`${funnelTotal} POS in scope`}
            stages={[
              {
                label: 'Prospect',
                count: funnel.prospect,
                color: 'var(--indigo)'
              },
              { label: 'To Win', count: funnel.towin, color: 'var(--amber)' },
              { label: 'Active', count: funnel.active, color: 'var(--olive)' },
              { label: 'Lost', count: funnel.lost, color: 'var(--crimson)' }
            ]}
          />
        </OverviewPipeline>
      </OverviewCard>

      {/* Tabs */}
      <Toolbar>
        <TabBar
          tabs={[
            {
              id: 'pos',
              label: `Points of Sale (${total})`,
              icon: (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 9l1-5h16l1 5" />
                  <path d="M4 9v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" />
                  <path d="M3 9h18" />
                  <path d="M10 13h4" />
                </svg>
              )
            },
            {
              id: 'lists',
              label: `Action Lists (${actionLists.length})`,
              icon: (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              )
            },
            {
              id: 'activity',
              label: `Activity (${activity.length})`,
              icon: (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
              )
            }
          ]}
          activeTab={activeTab}
          onTabChange={(id) => setActiveTab(id as CrmTab)}
        />
      </Toolbar>

      {/* Tab Content */}
      {activeTab === 'pos' && (
        <TableWrap>
          <TableHead>
            <span>
              <strong>{total}</strong> POS · Sorted by <strong>priority</strong>
            </span>
            <FilterTrigger
              type="button"
              onClick={() => setFilterModalOpen(true)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <FilterBadge>{activeFilterCount}</FilterBadge>
              )}
            </FilterTrigger>
            <ColDisplayTrigger type="button">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
              Column display <ColDisplayBadge>6</ColDisplayBadge>
            </ColDisplayTrigger>
          </TableHead>
          <Table>
            <thead>
              <tr>
                <ThCheckbox>
                  <RowCheck
                    $checked={selectedIds.size === pos.length}
                    $indeterminate={
                      selectedIds.size > 0 && selectedIds.size < pos.length
                    }
                    onClick={toggleSelectAll}
                  />
                </ThCheckbox>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th $align="right">Your refs</Th>
                <Th $align="right">Total refs</Th>
                <Th $align="right">Competitors</Th>
                <Th>Last change</Th>
                <Th $align="right">Evolution</Th>
              </tr>
            </thead>
            <tbody>
              {pos.map((p) => (
                <Tr
                  key={p.id}
                  onClick={() => handlePosClick(p)}
                  $selected={selectedIds.has(p.id)}
                >
                  <TdCheckbox>
                    <RowCheck
                      $checked={selectedIds.has(p.id)}
                      onClick={(e) => toggleSelection(p.id, e)}
                    />
                  </TdCheckbox>
                  <Td>
                    <PosNameCell>
                      <PosName>{p.name}</PosName>
                      <PosCity>
                        {p.city} · {p.type}
                      </PosCity>
                    </PosNameCell>
                  </Td>
                  <Td>
                    <Badge variant={statusToVariant(p.status)} dot>
                      {p.status === 'towin' ? 'To Win' : p.status}
                    </Badge>
                  </Td>
                  <TdNum>{p.yourRef}</TdNum>
                  <TdNum>{p.totalRefs}</TdNum>
                  <TdNum>{p.competitors}</TdNum>
                  <Td>
                    <LastChange>{p.lastChange}</LastChange>
                  </Td>
                  <TdNum>
                    <Evolution
                      $positive={p.evolution > 0}
                      $negative={p.evolution < 0}
                    >
                      {p.evolution > 0 ? '+' : ''}
                      {p.evolution}
                    </Evolution>
                  </TdNum>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {activeTab === 'lists' && (
        <TableWrap>
          <TableHead>
            <span>
              <strong>{actionLists.length}</strong> saved action lists · Sorted
              by <strong>most recent</strong>
            </span>
            <SearchInput
              placeholder="Search by name, recipient, or action…"
              value={searchQuery}
              onChange={(val) => setSearchQuery(val)}
            />
          </TableHead>
          <ActionListsGrid>
            {actionLists
              .filter(
                (l) =>
                  !searchQuery ||
                  l.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  l.sentTo.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  l.action.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((list) => {
                const prog = listProgress(list);
                return (
                  <ActionListCard
                    key={list.id}
                    onClick={() => handleListClick(list)}
                  >
                    <ActionListCardHead>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <ActionListNameRow>{list.name}</ActionListNameRow>
                        <ActionListCardMeta>
                          <PrivacyPill $variant={list.privacy}>
                            {list.privacy === 'private' ? (
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  x="3"
                                  y="11"
                                  width="18"
                                  height="11"
                                  rx="2"
                                />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                              </svg>
                            ) : (
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                              </svg>
                            )}
                            {list.privacy === 'private' ? 'Private' : 'Shared'}
                          </PrivacyPill>
                          <MetaDot />
                          <span>{formatListDate(list.createdAt)}</span>
                          <MetaDot />
                          <span>by {list.createdBy}</span>
                        </ActionListCardMeta>
                      </div>
                      <ActionListCountBadge>
                        {prog.total} POS
                      </ActionListCountBadge>
                    </ActionListCardHead>
                    <ActionListInfoBox>
                      <ActionListInfoRow>
                        <ActionListInfoLabel>Sent to</ActionListInfoLabel>
                        <ActionListInfoValue>
                          {list.sentTo}{' '}
                          <span
                            style={{
                              color: 'var(--ink-faint)',
                              fontWeight: 400,
                              fontSize: '11.5px'
                            }}
                          >
                            ·{' '}
                            {list.sentToType === 'distributor'
                              ? 'Distributor'
                              : 'Sales team'}
                          </span>
                        </ActionListInfoValue>
                      </ActionListInfoRow>
                      <ActionListInfoRow>
                        <ActionListInfoLabel>Action</ActionListInfoLabel>
                        <ActionListInfoValue>{list.action}</ActionListInfoValue>
                      </ActionListInfoRow>
                    </ActionListInfoBox>
                    <ActionListProgressRow>
                      <ActionListProgressStat $tone="pending">
                        <strong>{prog.toWinTotal}</strong>
                        <span>To Win</span>
                      </ActionListProgressStat>
                      <ActionListProgressStat $tone="positive">
                        <strong>{prog.active}</strong>
                        <span>Active</span>
                      </ActionListProgressStat>
                      <ActionListProgressStat $tone="negative">
                        <strong>{prog.lost}</strong>
                        <span>Lost</span>
                      </ActionListProgressStat>
                    </ActionListProgressRow>
                  </ActionListCard>
                );
              })}
          </ActionListsGrid>
        </TableWrap>
      )}

      {/* Action List Detail — lateral panel */}
      {selectedList && (
        <Drawer
          open={listDetailOpen}
          onClose={() => setListDetailOpen(false)}
          eyebrow={`ACTION LIST · ${
            selectedList.privacy === 'private' ? 'PRIVATE' : 'SHARED'
          }`}
          title={selectedList.name}
          footer={
            <DetailFooter>
              <Button onClick={() => setListDetailOpen(false)}>Close</Button>
            </DetailFooter>
          }
        >
          <DetailSubtitle>
            Sent to {selectedList.sentTo}{' '}
            <span>
              ·{' '}
              {selectedList.sentToType === 'distributor'
                ? 'Distributor'
                : 'Sales team'}
            </span>{' '}
            · {selectedList.action} · {formatListDate(selectedList.createdAt)}{' '}
            by {selectedList.createdBy}
          </DetailSubtitle>
          <DetailStatsStrip>
            <DetailStat>
              <DetailStatValue>
                {listProgress(selectedList).total}
              </DetailStatValue>
              <DetailStatLabel>Total POS</DetailStatLabel>
            </DetailStat>
            <DetailStat>
              <DetailStatValue $color="pending">
                {listProgress(selectedList).toWinTotal}
              </DetailStatValue>
              <DetailStatLabel>To Win</DetailStatLabel>
            </DetailStat>
            <DetailStat>
              <DetailStatValue $color="positive">
                {listProgress(selectedList).active}
              </DetailStatValue>
              <DetailStatLabel>Active</DetailStatLabel>
            </DetailStat>
            <DetailStat>
              <DetailStatValue $color="negative">
                {listProgress(selectedList).lost}
              </DetailStatValue>
              <DetailStatLabel>Lost</DetailStatLabel>
            </DetailStat>
            <DetailStat>
              <DetailStatValue>
                {listProgress(selectedList).successRate !== null
                  ? `${listProgress(selectedList).successRate}%`
                  : '—'}
              </DetailStatValue>
              <DetailStatLabel>Success rate</DetailStatLabel>
            </DetailStat>
          </DetailStatsStrip>
          <DetailTableWrap>
            <DetailTable>
              <thead>
                <tr>
                  <DetailTh>POS</DetailTh>
                  <DetailTh>City</DetailTh>
                  <DetailTh>At creation</DetailTh>
                  <DetailTh>Current</DetailTh>
                  <DetailTh>Change</DetailTh>
                </tr>
              </thead>
              <tbody>
                {selectedList.pos.map((p) => {
                  const change = classifyChange(
                    p.initialStatus,
                    p.currentStatus
                  );
                  return (
                    <DetailTr
                      key={p.id}
                      $highlight={change.type === 'positive'}
                    >
                      <DetailTd>
                        <strong>{p.name}</strong>
                      </DetailTd>
                      <DetailTd $muted>{p.city}</DetailTd>
                      <DetailTd>
                        <Badge variant={statusToVariant(p.initialStatus)} dot>
                          {p.initialStatus === 'towin'
                            ? 'To Win'
                            : p.initialStatus}
                        </Badge>
                      </DetailTd>
                      <DetailTd>
                        <StatusCompare>
                          <StatusArrow>
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <line x1="5" y1="12" x2="19" y2="12" />
                              <polyline points="12 5 19 12 12 19" />
                            </svg>
                          </StatusArrow>
                          <Badge variant={statusToVariant(p.currentStatus)} dot>
                            {p.currentStatus === 'towin'
                              ? 'To Win'
                              : p.currentStatus}
                          </Badge>
                        </StatusCompare>
                      </DetailTd>
                      <DetailTd>
                        <ChangePill $tone={change.type}>
                          {change.label}
                        </ChangePill>
                      </DetailTd>
                    </DetailTr>
                  );
                })}
              </tbody>
            </DetailTable>
          </DetailTableWrap>
          {selectedList.notes && (
            <DetailNotes>
              <DetailNotesLabel>Notes</DetailNotesLabel>
              {selectedList.notes}
            </DetailNotes>
          )}
        </Drawer>
      )}

      {activeTab === 'activity' && (
        <TableWrap>
          <TableHead>
            <span>
              <strong>{activity.length}</strong> recent activities · across all
              account users
            </span>
            <SearchInput
              placeholder="Search by user, target, or content…"
              value={searchQuery}
              onChange={(val) => setSearchQuery(val)}
            />
            <ActivityFilterRow>
              <ActivityFilterLabel>Filter by user</ActivityFilterLabel>
              <ActivityUserSelect
                value={activityUserFilter}
                onChange={(e) => setActivityUserFilter(e.target.value)}
              >
                <option value="all">All users</option>
                {activityUsers.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </ActivityUserSelect>
            </ActivityFilterRow>
          </TableHead>
          <ActivityFeed
            items={activity
              .filter(
                (a) =>
                  (activityUserFilter === 'all' ||
                    a.user === activityUserFilter) &&
                  (!searchQuery ||
                    a.user.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    a.target.toLowerCase().includes(searchQuery.toLowerCase()))
              )
              .map(
                (a): ActivityItem => ({
                  id: a.id,
                  initials: a.userInitials,
                  user: a.user,
                  kind: a.kind,
                  action: a.action,
                  target: a.target,
                  targetType: a.targetType,
                  targetId: a.targetId,
                  detail: a.detail,
                  when: a.when,
                  privacy: a.privacy,
                  with_: a.with_,
                  posCount: a.posCount,
                  sentTo: a.sentTo,
                  recipientCount: a.recipientCount
                })
              )}
            onItemClick={handleActivityClick}
          />
        </TableWrap>
      )}

      {/* Selection Bar */}
      <SelectionBar $visible={selectedIds.size > 0 && activeTab === 'pos'}>
        <SelectionCount>
          <strong>{selectedIds.size}</strong> POS selected
        </SelectionCount>
        <SelectionDivider />
        <SelectionClear onClick={clearSelection}>Clear</SelectionClear>
        <SelectionAction type="button" onClick={() => setEmailModalOpen(true)}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
            <polyline points="22 6 12 13 2 6" />
          </svg>
          Write an email
        </SelectionAction>
        <SelectionSave
          type="button"
          onClick={() => setActionListModalOpen(true)}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
          Save as Action List
        </SelectionSave>
      </SelectionBar>

      {/* Drawer */}
      <PosDrawer
        pos={selectedPos}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      {/* Activity Detail Drawer */}
      <ActivityDetailDrawer
        item={activityDetailItem}
        open={activityDetailOpen}
        onClose={() => setActivityDetailOpen(false)}
      />

      {/* Modals */}
      <NewPosModal
        open={newPosOpen}
        onClose={() => setNewPosOpen(false)}
        onSubmit={(data) => console.log('New POS:', data)}
      />
      <UploadListModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <PosFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Filter your accounts"
        posCount={total}
        filters={posFilters}
        onChange={handleFilterChange}
        onReset={handleFilterReset}
      />
      <WriteEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        selectedPos={pos.filter((p) => selectedIds.has(p.id))}
        onSend={(data) => {
          console.log('Email sent:', data);
          clearSelection();
        }}
      />
      <SaveActionListModal
        open={actionListModalOpen}
        onClose={() => setActionListModalOpen(false)}
        selectedPos={pos.filter((p) => selectedIds.has(p.id))}
        onSave={(data) => {
          const selected = pos.filter((p) => selectedIds.has(p.id));
          createActionList.mutate(
            toCreateActionListBody({
              name: data.name,
              action: data.action,
              notes: data.notes,
              sentTo: data.sentTo,
              sentToType: data.sentToType,
              privacy: data.privacy,
              storeIds: selected.map((p) => p.id)
            }),
            {
              onSuccess: () => {
                refetch();
                clearSelection();
                setActionListModalOpen(false);
              },
              onError: (error) => {
                // eslint-disable-next-line no-console
                console.error('Failed to save action list:', error);
              }
            }
          );
        }}
      />
    </Page>
  );
};

// ─── Icons ──────────────────────────────────────────────────────────────────

const ExportIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);
const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

// ─── Styled Components ──────────────────────────────────────────────────────

const Page = styled.div`
  max-width: 1280px;
  padding: 0;
`;

// ─── Summary / KPI ──────────────────────────────────────────────────────────

const OverviewCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  overflow: hidden;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const OverviewKpis = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: var(--line);
  border-right: 1px solid var(--line);

  @media (max-width: 900px) {
    border-right: none;
    border-bottom: 1px solid var(--line);
  }
`;

const KpiCell = styled.div`
  background: var(--white);
  padding: 18px 20px;
`;

const KpiLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-faint);
  margin-bottom: 8px;
`;

const KpiValue = styled.div`
  font-size: 28px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--ink);
  margin-bottom: 6px;
`;

const KpiDelta = styled.div<{ $down?: boolean }>`
  font-size: 11.5px;
  color: ${({ $down }) => ($down ? 'var(--crimson)' : 'var(--ink-faint)')};
`;

const OverviewPipeline = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// ─── Tabs / Toolbar ─────────────────────────────────────────────────────────

const Toolbar = styled.section`
  border-bottom: 1px solid var(--line);
  margin-bottom: 0;
`;

// ─── Table (POS tab) ────────────────────────────────────────────────────────

const TableWrap = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-top: none;
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
  overflow: hidden;
`;

const TableHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  font-size: 13px;
  color: var(--ink-muted);
  border-bottom: 1px solid var(--line);
  flex-wrap: wrap;
`;

const FilterTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--terracotta);
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--terracotta);
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: var(--terracotta-soft);
  }
`;

const FilterBadge = styled.span`
  background: var(--terracotta);
  color: var(--white);
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 999px;
  font-variant-numeric: tabular-nums;
`;

const ColDisplayTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 6px 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--ink-muted);
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    border-color: var(--ink-faint);
    color: var(--ink);
  }
`;

const ColDisplayBadge = styled.span`
  background: var(--ink);
  color: var(--white);
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  margin-left: 2px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th<{ $align?: string }>`
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-faint);
  text-align: ${({ $align }) => $align || 'left'};
  border-bottom: 1px solid var(--line);
  white-space: nowrap;
`;

const Tr = styled.tr<{ $selected?: boolean }>`
  cursor: pointer;
  transition: background 0.1s;
  background: ${({ $selected }) =>
    $selected
      ? 'var(--terracotta-soft, rgba(200, 75, 31, 0.06))'
      : 'transparent'};

  &:hover {
    background: ${({ $selected }) =>
      $selected
        ? 'var(--terracotta-soft, rgba(200, 75, 31, 0.08))'
        : 'var(--paper)'};
  }

  &:not(:last-child) td {
    border-bottom: 1px solid var(--line);
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 13px;
  color: var(--ink);
  vertical-align: middle;
`;

const TdNum = styled(Td)`
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const PosNameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PosName = styled.span`
  font-weight: 500;
  color: var(--ink);
`;

const PosCity = styled.span`
  font-size: 11.5px;
  color: var(--ink-faint);
`;

const LastChange = styled.span`
  font-size: 12px;
  color: var(--ink-muted);
`;

const Evolution = styled.span<{ $positive?: boolean; $negative?: boolean }>`
  font-family: var(--font-mono);
  font-size: 12px;
  color: ${({ $positive, $negative }) =>
    $positive
      ? 'var(--olive)'
      : $negative
        ? 'var(--crimson)'
        : 'var(--ink-muted)'};
`;

// ─── Checkbox / Selection ───────────────────────────────────────────────────

const ThCheckbox = styled.th`
  width: 36px;
  padding: 12px 0 12px 16px;
  border-bottom: 1px solid var(--line);
`;

const TdCheckbox = styled.td`
  width: 36px;
  padding: 12px 0 12px 16px;
  vertical-align: middle;
`;

const RowCheck = styled.div<{ $checked?: boolean; $indeterminate?: boolean }>`
  width: 16px;
  height: 16px;
  border: 1.5px solid
    ${({ $checked, $indeterminate }) =>
      $checked || $indeterminate ? 'var(--ink)' : 'var(--line)'};
  border-radius: 3px;
  cursor: pointer;
  display: grid;
  place-items: center;
  background: ${({ $checked, $indeterminate }) =>
    $checked || $indeterminate ? 'var(--ink)' : 'var(--white)'};
  transition: all 0.1s;

  &:hover {
    border-color: var(--ink-muted);
  }

  &::after {
    content: '${({ $checked, $indeterminate }) =>
      $checked ? '✓' : $indeterminate ? '−' : ''}';
    color: var(--white);
    font-size: 11px;
    line-height: 1;
  }
`;

const SelectionBar = styled.div<{ $visible: boolean }>`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%)
    ${({ $visible }) => ($visible ? 'translateY(0)' : 'translateY(100px)')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  background: #2b3d54;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(43, 61, 84, 0.35);
  z-index: 50;
  transition:
    transform 0.25s ease,
    opacity 0.2s ease;
`;

const SelectionCount = styled.span`
  font-size: 13px;
  color: var(--white);
  white-space: nowrap;
`;

const SelectionDivider = styled.div`
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
`;

const SelectionClear = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition:
    color 0.15s,
    background 0.15s;

  &:hover {
    color: var(--white);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SelectionAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--white);
  color: var(--ink);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 8px 14px;
  border-radius: 8px;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: var(--paper);
    border-color: var(--line);
  }
`;

const SelectionSave = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--terracotta);
  color: var(--white);
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    transform 0.15s;
  white-space: nowrap;

  svg {
    width: 15px;
    height: 15px;
  }

  &:hover {
    background: var(--terracotta-dark, #b0411a);
    transform: translateY(-1px);
  }
`;

// ─── Action Lists tab ───────────────────────────────────────────────────────

const ActionListsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
  padding: 20px 26px 28px;
`;

const ActionListCard = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 18px 18px 14px;
  cursor: pointer;
  transition:
    border-color 0.15s,
    transform 0.15s,
    box-shadow 0.18s;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &:hover {
    border-color: var(--terracotta);
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(43, 61, 84, 0.08);
  }
`;

const ActionListCardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const ActionListNameRow = styled.div`
  font-family: var(--font-serif);
  font-size: 16px;
  font-weight: 500;
  color: var(--ink);
  line-height: 1.3;
  margin-bottom: 4px;
`;

const ActionListCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 11.5px;
  color: var(--ink-faint);
`;

const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  background: var(--ink-faint);
  border-radius: 50%;
`;

const PrivacyPill = styled.span<{ $variant: 'private' | 'shared' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px 2px 6px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  svg {
    width: 11px;
    height: 11px;
    flex-shrink: 0;
  }

  ${({ $variant }) =>
    $variant === 'private'
      ? `background: var(--paper-warm); color: var(--ink-muted); border: 1px solid var(--line);`
      : `background: rgba(58, 74, 107, 0.10); color: var(--indigo); border: 1px solid rgba(58, 74, 107, 0.18);`}
`;

const ActionListCountBadge = styled.span`
  background: #2b3d54;
  color: var(--white);
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ActionListInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  background: var(--paper);
  border-radius: 8px;
  border-left: 3px solid var(--terracotta);
`;

const ActionListInfoRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-size: 12.5px;
  line-height: 1.4;
`;

const ActionListInfoLabel = styled.span`
  color: var(--ink-faint);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  min-width: 60px;
  flex-shrink: 0;
`;

const ActionListInfoValue = styled.span`
  color: var(--ink);
  font-weight: 500;
`;

const ActionListProgressRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  border-top: 1px solid var(--line-soft, var(--line));
  padding-top: 12px;
`;

type Tone = 'positive' | 'negative' | 'pending' | 'neutral';

const toneColors: Record<Tone, string> = {
  positive: 'var(--olive)',
  negative: 'var(--crimson)',
  pending: 'var(--amber)',
  neutral: 'var(--ink-faint)'
};

const ActionListProgressStat = styled.div<{ $tone: Tone }>`
  text-align: center;
  padding: 6px;
  border-radius: 6px;

  strong {
    display: block;
    font-size: 18px;
    font-weight: 600;
    font-family: var(--font-serif);
    font-variant-numeric: tabular-nums;
    line-height: 1;
    color: ${({ $tone }) => toneColors[$tone]};
  }

  span {
    display: block;
    font-size: 10.5px;
    color: var(--ink-faint);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-top: 4px;
    font-weight: 500;
  }
`;

// ─── Detail Popup Styled Components ─────────────────────────────────────────

const DetailFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const DetailSubtitle = styled.div`
  font-size: 13px;
  color: var(--ink-faint);
  margin-bottom: 16px;
  line-height: 1.5;
`;

const DetailStatsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(84px, 1fr));
  gap: 1px;
  background: var(--line);
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const DetailStat = styled.div`
  background: var(--white);
  padding: 12px 10px;
  text-align: center;
`;

const DetailStatValue = styled.div<{
  $color?: 'positive' | 'pending' | 'negative';
}>`
  font-size: 24px;
  font-family: var(--font-serif);
  font-weight: 500;
  color: ${({ $color }) =>
    $color === 'positive'
      ? 'var(--olive)'
      : $color === 'pending'
        ? 'var(--amber)'
        : $color === 'negative'
          ? 'var(--crimson)'
          : 'var(--ink)'};
  font-variant-numeric: tabular-nums;
  line-height: 1;
`;

const DetailStatLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  margin-top: 6px;
  font-weight: 500;
`;

const DetailTableWrap = styled.div`
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
  max-height: 380px;
  overflow-y: auto;
  margin-bottom: 16px;
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const DetailTh = styled.th`
  background: var(--paper);
  padding: 11px 16px;
  text-align: left;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--ink-muted);
  font-weight: 600;
  border-bottom: 1px solid var(--line);
  position: sticky;
  top: 0;
  z-index: 2;
`;

const DetailTr = styled.tr<{ $highlight?: boolean }>`
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(74, 107, 58, 0.04)' : 'transparent'};
`;

const DetailTd = styled.td<{ $muted?: boolean }>`
  padding: 12px 16px;
  border-bottom: 1px solid var(--line-soft, var(--line));
  font-size: 13px;
  color: ${({ $muted }) => ($muted ? 'var(--ink-faint)' : 'var(--ink)')};
  vertical-align: middle;

  &:last-child {
    border-bottom: none;
  }
`;

const StatusCompare = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusArrow = styled.span`
  color: var(--ink-faint);
  display: flex;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const ChangePill = styled.span<{
  $tone: 'positive' | 'negative' | 'pending' | 'neutral';
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  ${({ $tone }) =>
    $tone === 'positive'
      ? 'background: rgba(74, 107, 58, 0.10); color: var(--olive);'
      : $tone === 'pending'
        ? 'background: rgba(184, 134, 42, 0.12); color: var(--amber);'
        : $tone === 'negative'
          ? 'background: rgba(156, 42, 26, 0.10); color: var(--crimson);'
          : 'background: var(--paper); color: var(--ink-faint);'}
`;

const DetailNotes = styled.div`
  background: var(--paper);
  border-radius: 10px;
  padding: 14px 16px;
  font-size: 13px;
  color: var(--ink-muted);
  line-height: 1.55;
  border-left: 3px solid var(--terracotta);
`;

const DetailNotesLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  margin-bottom: 6px;
  font-weight: 600;
`;

// ─── Activity tab (now uses v2 ActivityFeed component) ──────────────────────

const ActivityFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ActivityFilterLabel = styled.label`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-muted);
`;

const ActivityUserSelect = styled.select`
  background: var(--white, #fff);
  border: 1px solid var(--line);
  border-radius: 7px;
  padding: 6px 26px 6px 12px;
  font-family: inherit;
  font-size: 12.5px;
  color: var(--ink);
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%237B8794' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 12px;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--ink-muted);
  }
  &:focus {
    border-color: var(--terracotta);
  }
`;
